// Reunderwriting Module (Tier 3) PR 3 — criteria checklist surface for
// the case-detail analyst tabs (A1 / A2 / A3 / A4).
//
// Cloned from components/DealFlow/CriteriaChecklist for ReunderwritingCase
// rather than Deal: hits /api/reunderwriting/cases/{caseId}/checklist via
// useReunderwritingChecklist. Surface:
//   - One row per checklist item, grouped by Tier (Deal Killers → Red
//     Flags → Needs Improvement).
//   - Per-row Open-Form modal carries the dynamic justification field,
//     the Severity Assessment selector (Clean / Minor / Material /
//     Critical), and the Report/Evidence Attestation checkbox.
//   - Certify / Decline are gated on assessment + attestation (server is
//     the source of truth; the UI mirrors the gate for fast feedback).
//   - Decline / Escalate / Waive modals collect a reason string.
//   - Waive is E2-only.

import { useState } from "react";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  useCertifyReunderwritingChecklistItem,
  useDeclineReunderwritingChecklistItem,
  useEscalateReunderwritingChecklistItem,
  useReunderwritingChecklist,
  useUpdateReunderwritingChecklistItem,
  useWaiveReunderwritingChecklistItem,
} from "@/hooks/reunderwriting/useReunderwriting";
import {
  REUNDERWRITING_PERMISSIONS,
  type CriteriaLane,
  type CriteriaTier,
  type ReunderwritingChecklistItemDto,
} from "@/types/reunderwriting";

const TIER_ORDER: ReadonlyArray<CriteriaTier> = ["DealKiller", "RedFlag", "NeedsImprovement"];
const TIER_HEADER: Record<CriteriaTier, string> = {
  DealKiller: "Deal Killers",
  RedFlag: "Red Flags",
  NeedsImprovement: "Needs Improvement",
};

const ASSESSMENT_VALUES = ["Clean", "Minor", "Material", "Critical"] as const;
type Assessment = (typeof ASSESSMENT_VALUES)[number];

interface ChecklistProps {
  caseId: string;
  lane: CriteriaLane;
  readOnly?: boolean;
}

export function ReunderwritingCriteriaChecklist({ caseId, lane, readOnly = false }: ChecklistProps) {
  const itemsQuery = useReunderwritingChecklist(caseId, lane);
  const items = itemsQuery.data ?? [];

  if (itemsQuery.isLoading) {
    return (
      <Card>
        <div
          className="flex flex-col gap-space-2"
          data-testid="reunderwriting-checklist-skeleton"
        >
          <div className="h-6 bg-white-warm animate-pulse rounded-md" />
          <div className="h-16 bg-white-warm animate-pulse rounded-md" />
          <div className="h-16 bg-white-warm animate-pulse rounded-md" />
        </div>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          No checklist items have been seeded for this case&apos;s {lane} lane yet.
        </p>
      </Card>
    );
  }

  const groups = TIER_ORDER.map((tier) => ({
    tier,
    items: items.filter((i) => i.tier === tier),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="flex flex-col gap-space-3" data-testid={`reunderwriting-checklist-${lane}`}>
      {groups.map((g) => (
        <section key={g.tier}>
          <h4 className="font-display text-h4 text-onyx m-0 mb-space-2">
            {TIER_HEADER[g.tier]}{" "}
            <span className="text-caption text-gray-mid font-normal">
              ({g.items.length})
            </span>
          </h4>
          <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
            {g.items.map((it) => (
              <ChecklistRow
                key={it.id}
                caseId={caseId}
                lane={lane}
                item={it}
                readOnly={readOnly}
              />
            ))}
          </ul>
        </section>
      ))}
      <Card>
        <ProgressSummary items={items} />
      </Card>
    </div>
  );
}

function ProgressSummary({ items }: { items: ReunderwritingChecklistItemDto[] }) {
  const counts = items.reduce(
    (c, i) => {
      c[i.status] = (c[i.status] ?? 0) + 1;
      return c;
    },
    {} as Record<string, number>,
  );
  const closed = (counts.Approved ?? 0) + (counts.Declined ?? 0) + (counts.Waived ?? 0);
  return (
    <div
      className="flex flex-wrap items-center gap-space-3 text-small text-onyx"
      data-testid="reunderwriting-checklist-progress"
    >
      <span className="font-display text-label uppercase tracking-label text-gold-shadow">
        Progress
      </span>
      <span>{closed} of {items.length} closed</span>
      <span className="text-gray-mid">·</span>
      <span>{counts.Approved ?? 0} Approved</span>
      <span>{counts.Declined ?? 0} Declined</span>
      <span>{counts.Waived ?? 0} Waived</span>
      <span>{counts.Escalated ?? 0} Escalated</span>
      <span>{counts.Pending ?? 0} Pending</span>
    </div>
  );
}

interface RowProps {
  caseId: string;
  lane: CriteriaLane;
  item: ReunderwritingChecklistItemDto;
  readOnly: boolean;
}

function ChecklistRow({ caseId, lane, item, readOnly }: RowProps) {
  const [formOpen, setFormOpen] = useState(false);

  return (
    <li
      className="border border-border-light rounded-md bg-white-pure px-space-3 py-space-2"
      data-testid={`reunderwriting-checklist-item-${item.catalogCode}`}
      data-status={item.status}
    >
      <div className="flex items-start gap-space-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-space-2 mb-space-1">
            <Badge status="draft">{item.catalogCode}</Badge>
            <TierBadge tier={item.tier} />
            <StatusBadge status={item.status} />
            {item.assessment && (
              <Badge status={item.reportUploadedAttested ? "finalized" : "archived"}>
                {item.assessment}
                {!item.reportUploadedAttested ? " · no attest" : ""}
              </Badge>
            )}
          </div>
          <div className="text-small text-onyx font-medium" title={item.catalogTitle}>
            {item.catalogTitle}
          </div>
          {item.status === "Declined" && item.failureReason && (
            <p className="mt-space-2 text-caption text-status-error m-0">
              Decline reason: {item.failureReason}
            </p>
          )}
          {item.status === "Waived" && item.waiverRationale && (
            <p className="mt-space-2 text-caption text-gray-mid italic m-0">
              Waiver rationale: {item.waiverRationale}
            </p>
          )}
        </div>
        <div className="flex items-center gap-space-1 shrink-0">
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setFormOpen(true)}
          >
            {item.status === "Pending" ? "Open form" : "View form"}
          </Button>
        </div>
      </div>

      {formOpen && (
        <OpenFormModal
          caseId={caseId}
          lane={lane}
          item={item}
          readOnly={readOnly}
          onClose={() => setFormOpen(false)}
        />
      )}
    </li>
  );
}

function TierBadge({ tier }: { tier: CriteriaTier }) {
  switch (tier) {
    case "DealKiller":
      return <Badge status="error">Deal Killer</Badge>;
    case "RedFlag":
      return <Badge status="archived">Red Flag</Badge>;
    case "NeedsImprovement":
      return <Badge status="draft">Needs Improvement</Badge>;
  }
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "Approved": return <Badge status="finalized">Approved</Badge>;
    case "Declined": return <Badge status="error">Declined</Badge>;
    case "Waived":   return <Badge status="gold">Waived</Badge>;
    case "Escalated":return <Badge status="archived">Escalated</Badge>;
    default:         return <Badge status="draft">Pending</Badge>;
  }
}

// ---------------------------------------------------------------------------
// Open-Form modal — dynamic justification field + Severity Assessment +
// Report/Evidence Attestation + per-action buttons (Certify / Decline /
// Escalate / Waive).
// ---------------------------------------------------------------------------

interface ModalProps {
  caseId: string;
  lane: CriteriaLane;
  item: ReunderwritingChecklistItemDto;
  readOnly: boolean;
  onClose: () => void;
}

function OpenFormModal({ caseId, lane, item, readOnly, onClose }: ModalProps) {
  const toast = useToast();
  const auth = useAuth();
  const permSet = new Set(auth.user?.permissions ?? []);
  const isE2 = auth.hasRoleCode?.("E2") ?? false;

  const [justification, setJustification] = useState(item.justification ?? "");
  const [assessment, setAssessment] = useState<Assessment | "">(
    (item.assessment as Assessment | null) ?? "",
  );
  const [attested, setAttested] = useState(item.reportUploadedAttested);
  const [actionForm, setActionForm] = useState<null | "decline" | "escalate" | "waive">(null);
  const [reason, setReason] = useState("");

  const update = useUpdateReunderwritingChecklistItem(caseId, lane);
  const certify = useCertifyReunderwritingChecklistItem(caseId, lane);
  const decline = useDeclineReunderwritingChecklistItem(caseId, lane);
  const escalate = useEscalateReunderwritingChecklistItem(caseId, lane);
  const waive = useWaiveReunderwritingChecklistItem(caseId, lane);

  const isTerminal =
    item.status === "Approved" || item.status === "Declined" || item.status === "Waived";
  const inputsDisabled = readOnly || isTerminal;

  // Underwriting spec §7.2 — Certify needs assessment + attestation.
  // Decline needs attestation.
  const certifyBlocked = !assessment || !attested;
  const declineBlocked = !attested;

  async function persistChanges() {
    if (
      justification === (item.justification ?? "") &&
      assessment === ((item.assessment as string | null) ?? "") &&
      attested === item.reportUploadedAttested
    ) {
      return; // no-op
    }
    await update.mutateAsync({
      itemId: item.id,
      body: {
        justification: justification || undefined,
        assessment: assessment || undefined,
        reportUploadedAttested: attested,
      },
    });
  }

  async function onSave() {
    try {
      await persistChanges();
      toast.success(`${item.catalogCode} saved.`);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function onCertify() {
    try {
      await persistChanges();
      await certify.mutateAsync(item.id);
      toast.success(`${item.catalogCode} certified.`);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function onSubmitAction() {
    if (!reason.trim()) {
      toast.error("Reason is required.");
      return;
    }
    try {
      await persistChanges();
      if (actionForm === "decline") {
        await decline.mutateAsync({ itemId: item.id, failureReason: reason });
        toast.success(`${item.catalogCode} declined. Failure tracking record created.`);
      } else if (actionForm === "escalate") {
        await escalate.mutateAsync({ itemId: item.id, escalationReason: reason });
        toast.success(`${item.catalogCode} escalated.`);
      } else if (actionForm === "waive") {
        await waive.mutateAsync({ itemId: item.id, rationale: reason });
        toast.success(`${item.catalogCode} waived.`);
      }
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${item.catalogCode} — ${item.catalogTitle}`}
      className="fixed inset-0 z-50 bg-onyx/40 flex items-center justify-center p-space-4"
      data-testid={`reunderwriting-form-modal-${item.catalogCode}`}
    >
      <div className="bg-white-pure rounded-md shadow-xl w-full max-w-[640px] max-h-[90vh] overflow-y-auto">
        <header className="border-b border-border-light px-space-4 py-space-3 flex items-start justify-between gap-space-3">
          <div>
            <div className="flex items-center gap-space-2 mb-space-1">
              <Badge status="draft">{item.catalogCode}</Badge>
              <TierBadge tier={item.tier} />
              <StatusBadge status={item.status} />
            </div>
            <h3 className="font-display text-h3 m-0">{item.catalogTitle}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-mid hover:text-onyx text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="px-space-4 py-space-3 flex flex-col gap-space-3">
          {actionForm === null ? (
            <>
              <label className="flex flex-col gap-space-1">
                <span className="text-label uppercase tracking-label text-gold-shadow">
                  Justification
                </span>
                <textarea
                  className="border border-border-light rounded-md p-space-2 text-small min-h-[120px]"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  disabled={inputsDisabled}
                  placeholder="Analyst rationale, sourced findings, references…"
                  data-testid="reunderwriting-form-justification"
                />
              </label>

              <fieldset className="flex flex-col gap-space-1">
                <legend className="text-label uppercase tracking-label text-gold-shadow">
                  Severity Assessment <span className="text-status-error">*</span>
                </legend>
                <div className="flex flex-wrap gap-space-2">
                  {ASSESSMENT_VALUES.map((v) => (
                    <label
                      key={v}
                      className="inline-flex items-center gap-space-1 text-small"
                    >
                      <input
                        type="radio"
                        name={`assessment-${item.id}`}
                        checked={assessment === v}
                        onChange={() => setAssessment(v)}
                        disabled={inputsDisabled}
                        data-testid={`reunderwriting-form-assessment-${v.toLowerCase()}`}
                      />
                      {v}
                    </label>
                  ))}
                </div>
                <p className="text-caption text-gray-mid m-0">
                  Required to Certify. Mirrors Deal Flow §8.5.
                </p>
              </fieldset>

              <label className="inline-flex items-start gap-space-2 text-small">
                <input
                  type="checkbox"
                  checked={attested}
                  onChange={(e) => setAttested(e.target.checked)}
                  disabled={inputsDisabled}
                  data-testid="reunderwriting-form-attestation"
                />
                <span>
                  <strong>Report / Evidence Attestation.</strong> I attest the
                  supporting report has been uploaded to the case&apos;s
                  Documents tab. Required to Certify or Decline.
                </span>
              </label>
            </>
          ) : (
            <ActionPrompt
              action={actionForm}
              code={item.catalogCode}
              value={reason}
              onChange={setReason}
              onBack={() => { setActionForm(null); setReason(""); }}
              onSubmit={onSubmitAction}
              busy={decline.isPending || escalate.isPending || waive.isPending}
            />
          )}
        </div>

        {actionForm === null && (
          <footer className="border-t border-border-light px-space-4 py-space-3 flex flex-wrap items-center justify-end gap-space-2">
            <Button variant="tertiary" onClick={onClose}>Close</Button>
            {!inputsDisabled && (
              <Button
                variant="secondary"
                onClick={onSave}
                disabled={update.isPending}
              >
                Save
              </Button>
            )}
            {!inputsDisabled && item.status === "Pending" &&
              permSet.has(REUNDERWRITING_PERMISSIONS.CertifyChecklistItem) && (
              <Button
                variant="primary"
                onClick={onCertify}
                disabled={certifyBlocked || certify.isPending}
                title={certifyBlocked
                  ? "Select a Severity Assessment and confirm the report attestation before certifying."
                  : undefined}
                data-testid="reunderwriting-form-certify"
              >
                Certify
              </Button>
            )}
            {!inputsDisabled && item.status === "Pending" &&
              permSet.has(REUNDERWRITING_PERMISSIONS.DeclineChecklistItem) && (
              <Button
                variant="secondary"
                onClick={() => setActionForm("decline")}
                disabled={declineBlocked}
                title={declineBlocked
                  ? "Confirm the report attestation before declining."
                  : undefined}
                data-testid="reunderwriting-form-decline"
              >
                Decline…
              </Button>
            )}
            {!inputsDisabled && item.status === "Pending" &&
              permSet.has(REUNDERWRITING_PERMISSIONS.EscalateChecklistItem) && (
              <Button
                variant="secondary"
                onClick={() => setActionForm("escalate")}
                data-testid="reunderwriting-form-escalate"
              >
                Escalate…
              </Button>
            )}
            {!inputsDisabled && item.status !== "Waived" && isE2 &&
              permSet.has(REUNDERWRITING_PERMISSIONS.WaiveChecklistItem) && (
              <Button
                variant="secondary"
                onClick={() => setActionForm("waive")}
                data-testid="reunderwriting-form-waive"
              >
                Waive (E2)…
              </Button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

function ActionPrompt({
  action,
  code,
  value,
  onChange,
  onBack,
  onSubmit,
  busy,
}: {
  action: "decline" | "escalate" | "waive";
  code: string;
  value: string;
  onChange: (v: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  busy: boolean;
}) {
  const labels = {
    decline: {
      title: `Decline ${code}`,
      hint: "Required failure reason — surfaced to the analyst on the Failure Tracking page.",
      cta: "Decline",
    },
    escalate: {
      title: `Escalate ${code}`,
      hint: "Required escalation reason — paired with an E1/E2 directive on resolution.",
      cta: "Escalate",
    },
    waive: {
      title: `Waive ${code} (E2)`,
      hint: "Required executive rationale — recorded on the audit trail.",
      cta: "Waive",
    },
  } as const;
  const l = labels[action];

  return (
    <div className="flex flex-col gap-space-2" data-testid={`reunderwriting-form-${action}-prompt`}>
      <h4 className="font-display text-h4 m-0">{l.title}</h4>
      <textarea
        className="border border-border-light rounded-md p-space-2 text-small min-h-[120px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus
      />
      <p className="text-caption text-gray-mid m-0">{l.hint}</p>
      <div className="flex items-center justify-end gap-space-2">
        <Button variant="tertiary" onClick={onBack}>Back</Button>
        <Button onClick={onSubmit} disabled={busy || !value.trim()}>{l.cta}</Button>
      </div>
    </div>
  );
}
